import { useState, useRef, useCallback } from 'react';

interface UseVoiceInputOptions {
  backendUrl: string;
  onTranscription: (text: string) => void;
  onError?: (error: Error) => void;
}

/**
 * Records audio from the microphone via MediaRecorder, uploads it to the
 * backend STT endpoint (/api/stt), and returns the transcribed text.
 */
export function useVoiceInput({ backendUrl, onTranscription, onError }: UseVoiceInputOptions) {
  const [isRecording, setIsRecording] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      const recorder = new MediaRecorder(stream, { mimeType });
      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());

        const blob = new Blob(chunksRef.current, { type: mimeType });
        const form = new FormData();
        form.append('audio', blob, 'recording.webm');

        try {
          const res = await fetch(`${backendUrl}/api/stt`, { method: 'POST', body: form });
          if (!res.ok) throw new Error(`STT error ${res.status}`);
          const { text } = (await res.json()) as { text: string };
          if (text?.trim()) onTranscription(text.trim());
        } catch (err) {
          onError?.(err instanceof Error ? err : new Error('STT failed'));
        }
      };

      recorder.start();
      setIsRecording(true);
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error('Microphone access denied'));
    }
  }, [backendUrl, onTranscription, onError]);

  const stopRecording = useCallback(() => {
    recorderRef.current?.stop();
    setIsRecording(false);
  }, []);

  const toggleRecording = useCallback(() => {
    if (isRecording) stopRecording();
    else void startRecording();
  }, [isRecording, startRecording, stopRecording]);

  return { isRecording, toggleRecording };
}
